/**
 * Form field definitions and types
 * Defines the structure of dynamic forms across different IMOs
 */
import { IMO, CARRIER_SLUG_TO_CARRIER, CARRIER_SLUG_TO_IMO, CARRIER_TO_IMO, CARRIER_DISPLAY_NAMES, detectIMOs, } from "./carriers.js";
export var FieldType;
(function (FieldType) {
    FieldType["TEXT"] = "TEXT";
    FieldType["EMAIL"] = "EMAIL";
    FieldType["PHONE"] = "PHONE";
    FieldType["DATE"] = "DATE";
    FieldType["SELECT"] = "SELECT";
    FieldType["COMBOBOX"] = "COMBOBOX";
    FieldType["CHECKBOX"] = "CHECKBOX";
    FieldType["CHECKBOX_GROUP"] = "CHECKBOX_GROUP";
    FieldType["TEXTAREA"] = "TEXTAREA";
    FieldType["SSN"] = "SSN";
    FieldType["FILE"] = "FILE";
    FieldType["RADIO"] = "RADIO";
    FieldType["SIGNATURE"] = "SIGNATURE";
})(FieldType || (FieldType = {}));
export var FieldVisibility;
(function (FieldVisibility) {
    FieldVisibility["ALWAYS"] = "ALWAYS";
    FieldVisibility["CONDITIONAL"] = "CONDITIONAL";
    FieldVisibility["HIDDEN"] = "HIDDEN";
})(FieldVisibility || (FieldVisibility = {}));
/** Field types that are unusable at half width — long lists, wrapped text, drawing surfaces. */
const FULL_WIDTH_FIELD_TYPES = new Set([
    FieldType.CHECKBOX_GROUP,
    FieldType.TEXTAREA,
    FieldType.SIGNATURE,
    FieldType.FILE,
]);
export function resolveFieldWidth(field) {
    return field.width ?? (FULL_WIDTH_FIELD_TYPES.has(field.type) ? "full" : "half");
}
export const DYNAMIC_FORM_SCHEMA_VERSION = "4.0.0";
/** Retired field IDs — hidden from agent detail display but preserved in stored formData. */
export const DEPRECATED_FIELD_IDS = new Set([
    "bringingDownlineAgents",
    "downlineAgentCount",
    "downlineAgentNames",
    "submittedByFullName",
    "sendConfirmationTo",
    "isMobile",
    "signature",
]);
// ─── Constants ────────────────────────────────────────────────────────────────
export const US_STATES = [
    { label: "Alaska", value: "AK" },
    { label: "Alabama", value: "AL" },
    { label: "Arkansas", value: "AR" },
    { label: "Arizona", value: "AZ" },
    { label: "California", value: "CA" },
    { label: "Colorado", value: "CO" },
    { label: "Connecticut", value: "CT" },
    { label: "Delaware", value: "DE" },
    { label: "Florida", value: "FL" },
    { label: "Georgia", value: "GA" },
    { label: "Hawaii", value: "HI" },
    { label: "Iowa", value: "IA" },
    { label: "Idaho", value: "ID" },
    { label: "Illinois", value: "IL" },
    { label: "Indiana", value: "IN" },
    { label: "Kansas", value: "KS" },
    { label: "Kentucky", value: "KY" },
    { label: "Louisiana", value: "LA" },
    { label: "Massachusetts", value: "MA" },
    { label: "Maryland", value: "MD" },
    { label: "Maine", value: "ME" },
    { label: "Michigan", value: "MI" },
    { label: "Minnesota", value: "MN" },
    { label: "Missouri", value: "MO" },
    { label: "Mississippi", value: "MS" },
    { label: "Montana", value: "MT" },
    { label: "North Carolina", value: "NC" },
    { label: "North Dakota", value: "ND" },
    { label: "Nebraska", value: "NE" },
    { label: "New Hampshire", value: "NH" },
    { label: "New Jersey", value: "NJ" },
    { label: "New Mexico", value: "NM" },
    { label: "Nevada", value: "NV" },
    { label: "New York", value: "NY" },
    { label: "Ohio", value: "OH" },
    { label: "Oklahoma", value: "OK" },
    { label: "Oregon", value: "OR" },
    { label: "Pennsylvania", value: "PA" },
    { label: "Rhode Island", value: "RI" },
    { label: "South Carolina", value: "SC" },
    { label: "South Dakota", value: "SD" },
    { label: "Tennessee", value: "TN" },
    { label: "Texas", value: "TX" },
    { label: "Utah", value: "UT" },
    { label: "Virginia", value: "VA" },
    { label: "Vermont", value: "VT" },
    { label: "Washington", value: "WA" },
    { label: "Wisconsin", value: "WI" },
    { label: "West Virginia", value: "WV" },
    { label: "Wyoming", value: "WY" },
];
/**
 * All available carriers for contracting — derived from {@link CARRIER_TO_IMO}.
 * Presented alphabetically to agents; IMO bucketing is backend-only.
 */
export const PINNACLE_AVAILABLE_CARRIERS = Object.keys(CARRIER_TO_IMO)
    .map((carrier) => ({
    label: CARRIER_DISPLAY_NAMES[carrier],
    value: carrier.toLowerCase(),
}))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
/** Maps each carrier form value back to its IMO — derived from {@link CARRIER_TO_IMO}. */
export const CARRIER_VALUE_TO_IMO = CARRIER_SLUG_TO_IMO;
/** Maps each carrier form value back to the Carrier enum — derived from {@link CARRIER_TO_IMO}. */
export const CARRIER_VALUE_TO_CARRIER = CARRIER_SLUG_TO_CARRIER;
export const COMMISSION_LEVEL_OPTIONS = [
    "50.00%",
    "55.00%",
    "60.00%",
    "65.00%",
    "70.00%",
    "75.00%",
    "80.00%",
    "85.00%",
    "90.00%",
    "95.00%",
    "100.00%",
    "105.00%",
    "110.00%",
    "115.00%",
    "120.00%",
    "125.00%",
    "130.00%",
    "135.00%",
    "LOA",
].map((level) => ({ label: level, value: level }));
export const IMO_DISPLAY_NAMES = {
    [IMO.PREMIER_PRODUCERS_GROUP]: "Premier Producers Group",
    [IMO.PINNACLE]: "Pinnacle",
    [IMO.HEARTLAND]: "Heartland",
    [IMO.SMS]: "Senior Market Sales",
};
export function isHeartlandImo(imo) {
    return imo === IMO.HEARTLAND;
}
function conditionalRuleMatches(rule, formData) {
    const value = formData[rule.fieldId];
    switch (rule.operator) {
        case "equals":
            return value === rule.value;
        case "contains":
            return Array.isArray(value)
                ? Array.isArray(rule.value)
                    ? rule.value.some((expected) => value.includes(expected))
                    : value.includes(rule.value)
                : false;
        case "isTrue":
            return value === true || value === "true" || value === "yes";
        case "isFalse":
            return value === false || value === "false" || value === "no";
        default:
            return false;
    }
}
export function isFieldVisible(field, formData) {
    if (field.visibility === FieldVisibility.HIDDEN)
        return false;
    const rules = field.conditionalRules ?? [];
    if (rules.length === 0)
        return true;
    const hideRules = rules.filter((rule) => rule.action === "hide");
    if (hideRules.some((rule) => conditionalRuleMatches(rule, formData)))
        return false;
    const showRules = rules.filter((rule) => rule.action === "show");
    if (field.visibility === FieldVisibility.CONDITIONAL || showRules.length > 0) {
        return showRules.some((rule) => conditionalRuleMatches(rule, formData));
    }
    return true;
}
export function isFieldRequired(field, formData) {
    if (!isFieldVisible(field, formData))
        return false;
    const rules = field.conditionalRules ?? [];
    if (rules.some((rule) => rule.action === "optional" && conditionalRuleMatches(rule, formData))) {
        return false;
    }
    if (rules.some((rule) => rule.action === "require" && conditionalRuleMatches(rule, formData))) {
        return true;
    }
    return field.required;
}
// ─── Common field definitions ─────────────────────────────────────────────────
export const COMMON_FORM_FIELDS = {
    fullName: {
        id: "fullName",
        name: "fullName",
        label: "Full Name",
        type: FieldType.TEXT,
        required: true,
        placeholder: "Jane Smith",
        visibility: FieldVisibility.ALWAYS,
        autoComplete: "name",
        validation: { minLength: 2, maxLength: 100 },
    },
    firstName: {
        id: "firstName",
        name: "firstName",
        label: "First Name",
        type: FieldType.TEXT,
        required: true,
        placeholder: "Jane",
        visibility: FieldVisibility.ALWAYS,
        validation: { minLength: 2, maxLength: 50 },
    },
    lastName: {
        id: "lastName",
        name: "lastName",
        label: "Last Name",
        type: FieldType.TEXT,
        required: true,
        placeholder: "Smith",
        visibility: FieldVisibility.ALWAYS,
        validation: { minLength: 2, maxLength: 50 },
    },
    email: {
        id: "email",
        name: "email",
        label: "Email Address",
        type: FieldType.EMAIL,
        required: true,
        placeholder: "jane@example.com",
        visibility: FieldVisibility.ALWAYS,
    },
    phone: {
        id: "phone",
        name: "phone",
        label: "Phone Number",
        type: FieldType.PHONE,
        required: true,
        placeholder: "(555) 000-0000",
        visibility: FieldVisibility.ALWAYS,
        mask: "(XXX) XXX-XXXX",
    },
    ssn: {
        id: "ssn",
        name: "ssn",
        label: "Social Security Number",
        type: FieldType.SSN,
        required: true,
        visibility: FieldVisibility.ALWAYS,
        encrypted: true,
        mask: "XXX-XX-XXXX",
        validation: { pattern: "^\\d{3}-?\\d{2}-?\\d{4}$" },
    },
    dateOfBirth: {
        id: "dateOfBirth",
        name: "dateOfBirth",
        label: "Date of Birth",
        type: FieldType.DATE,
        required: true,
        visibility: FieldVisibility.ALWAYS,
        encrypted: true,
        validation: { custom: "pastDate" },
    },
    address: {
        id: "address",
        name: "address",
        label: "Street Address",
        type: FieldType.TEXT,
        required: true,
        placeholder: "123 Main St",
        visibility: FieldVisibility.ALWAYS,
    },
    addressLine2: {
        id: "addressLine2",
        name: "addressLine2",
        label: "Address Line 2",
        type: FieldType.TEXT,
        required: false,
        placeholder: "Apt, Suite, Unit…",
        visibility: FieldVisibility.ALWAYS,
    },
    // City / State / ZIP tile as one 4+4+4 row.
    city: {
        id: "city",
        name: "city",
        label: "City",
        type: FieldType.TEXT,
        required: true,
        placeholder: "Austin",
        visibility: FieldVisibility.ALWAYS,
        width: "third",
    },
    state: {
        id: "state",
        name: "state",
        label: "State",
        type: FieldType.COMBOBOX,
        required: true,
        placeholder: "Search states…",
        visibility: FieldVisibility.ALWAYS,
        options: US_STATES,
        width: "third",
    },
    zipCode: {
        id: "zipCode",
        name: "zipCode",
        label: "ZIP Code",
        type: FieldType.TEXT,
        required: true,
        placeholder: "78701",
        visibility: FieldVisibility.ALWAYS,
        validation: { pattern: "^\\d{5}(-\\d{4})?$" },
        width: "third",
    },
};
export const IDENTITY_FORM_SECTION = {
    id: "agent_info",
    title: "Your Information",
    description: "Start with the core applicant details. We'll email you a link to continue later.",
    fields: [
        COMMON_FORM_FIELDS.fullName,
        COMMON_FORM_FIELDS.email,
        COMMON_FORM_FIELDS.phone,
    ],
};
export const CARRIER_SELECTION_SECTION = {
    id: "carriers_to_contract",
    title: "Carriers to Contract",
    description: "Select the carriers you want to contract with. This list is a reference. The contract itself is completed in SureLC.",
    fields: [
        {
            id: "selectedCarriers",
            name: "selectedCarriers",
            label: "Select Carriers",
            type: FieldType.CHECKBOX_GROUP,
            required: true,
            visibility: FieldVisibility.ALWAYS,
            options: PINNACLE_AVAILABLE_CARRIERS,
            gridCols: 3,
        },
    ],
    wide: true,
};
/** @deprecated Use IDENTITY_FORM_SECTION + CARRIER_SELECTION_SECTION — kept for migration compatibility. */
export const BASE_FORM_SECTION = {
    id: "agent_info",
    title: "New Agent Registration",
    description: "Start with the core applicant details and carriers to contract.",
    fields: [
        ...IDENTITY_FORM_SECTION.fields,
        ...CARRIER_SELECTION_SECTION.fields,
    ],
    wide: true,
};
const NIPR_IDENTITY_SECTION = {
    id: "nipr_identity",
    title: "NIPR verification",
    description: "SureLC looks up your National Producer Number from your Social Security number and date of birth. You do not enter an NPN here.",
    fields: [COMMON_FORM_FIELDS.ssn, COMMON_FORM_FIELDS.dateOfBirth],
};
function buildApplicationReviewSection() {
    return {
        id: "application_review",
        title: "Review & Submit",
        description: "Review your carriers and NIPR identity before submitting. The contract is completed in SureLC.",
        fields: [
            {
                id: "reviewSummary",
                name: "reviewSummary",
                label: "Contract Request Summary",
                type: FieldType.TEXTAREA,
                required: false,
                visibility: FieldVisibility.ALWAYS,
                description: "Display-only review summary populated by the contract request UI.",
                helpText: "Selected carriers and NIPR identity appear here before submit.",
            },
        ],
    };
}
export function resolveFormRequirements(carrierValues) {
    const imos = detectIMOs(carrierValues);
    const needsReagan = imos.some(isHeartlandImo);
    const needsPinnacle = imos.includes(IMO.PINNACLE);
    const needsPPG = imos.includes(IMO.PREMIER_PRODUCERS_GROUP);
    const needsSMS = imos.includes(IMO.SMS);
    return {
        imos,
        needsLicenseIdentity: needsPinnacle || needsReagan,
        needsReagan,
        needsPinnacle,
        needsPPG,
        needsSMS,
    };
}
export function getReadinessChecklist(_requirements) {
    return ["Social Security number", "Date of birth"];
}
// Frontend and backend both call this resolver so the visible questions and server validation stay in sync.
export function buildFormSchemaForCarriers(carrierValues = []) {
    const requirements = resolveFormRequirements(carrierValues);
    // SureLC collects the carrier application. Altura only keeps the reference
    // carrier list and the identity NIPR uses to return an NPN.
    const sections = [
        IDENTITY_FORM_SECTION,
        CARRIER_SELECTION_SECTION,
        NIPR_IDENTITY_SECTION,
        buildApplicationReviewSection(),
    ];
    return {
        id: "dynamic_imo_form",
        version: DYNAMIC_FORM_SCHEMA_VERSION,
        imo: requirements.imos[0] ?? IMO.PINNACLE,
        carriers: carrierValues
            .map((carrier) => CARRIER_VALUE_TO_CARRIER[carrier])
            .filter((carrier) => Boolean(carrier)),
        title: "New Agent Registration",
        description: "Choose your carriers and confirm the identity SureLC uses to look up your NPN.",
        sections,
    };
}
/**
 * Builds the authenticated portal contracting form.
 *
 * Portal identity comes from the authenticated session, so the public-form
 * identity step must not be rendered or validated. Returning a new schema and
 * sections array keeps the legacy public schema unchanged.
 */
export function buildPortalContractSchemaForCarriers(carrierValues = []) {
    const schema = buildFormSchemaForCarriers(carrierValues);
    return {
        ...schema,
        sections: schema.sections.filter((section) => section.id !== IDENTITY_FORM_SECTION.id),
    };
}
export const PINNACLE_FORM_SCHEMA = buildFormSchemaForCarriers(["accendo"]);
